import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MaterialSymbol } from 'react-material-symbols';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';

import AuthLayout from '../../../components/AuthLayout.js';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../components/ui/form.tsx';
import { Input } from '../../../components/ui/input.tsx';
import useParent from '../../../hooks/useParent.js';
import { LoadingSpinner } from '../../../components/ui/loading-spinner.tsx';

const formSchema = z.object({
  email: z.string().email({
    message: 'Please enter a valid email address.',
  }),
});

const ParentDashboardForgotPassword = () => {
  const navigate = useNavigate();
  const {
    parentForgotPassword,
    parentForgotPasswordLoading,
    parentForgotPasswordSuccess,
  } = useParent();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (values) => {
    if (parentForgotPasswordLoading) return;

    try {
      await parentForgotPassword(values);
    } catch (error) {
      console.error('Forgot Password failed:', error);
      toast.error(error.message || 'Please try again later.');
      form.setError('root', {
        type: 'manual',
        message:
          error.message ||
          'Failed Forgot Password Process. Please try again later.',
      });
    }
  };

  useEffect(() => {
    let timer;
    if (parentForgotPasswordSuccess) {
      timer = setTimeout(() => {
        navigate('/parent/login');
      }, 5000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [parentForgotPasswordSuccess, navigate]);

  return (
    <AuthLayout>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col space-y-12 h-3/5 justify-center"
        >
          <div>
            <h1 className="font-medium text-2xl md:text-3xl">
              Forgot Password?
            </h1>
            <p className="text-[#828282] text-lg md:text-xl font-light">
              No worries, we will send you reset instructions.
            </p>
          </div>
          <div>
            {form.formState.errors.root && (
              <div className="p-3 text-sm text-red-600 bg-red-50 rounded-md">
                {form.formState.errors.root.message}
              </div>
            )}

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[#4d4d4d] text-base md:text-lg font-light">
                    Enter your E-mail here
                  </FormLabel>
                  <FormControl>
                    <span className="flex items-center border border-[#e5e5e5] rounded-sm h-10 px-2">
                      <MaterialSymbol
                        icon="mail"
                        size={24}
                        grade={0}
                        weight={300}
                        color="black"
                      />
                      <Input
                        placeholder="example@mail.com"
                        {...field}
                        disabled={parentForgotPasswordLoading}
                        className="outline-none border-none focus:outline-none focus:border-none focus-visible:ring-0 focus-visible:outline-none focus-visible:border-none rounded-none h-7"
                      />
                    </span>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {parentForgotPasswordSuccess && (
              <div className="p-3 text-sm text-green-600 bg-green-50 rounded-md mt-4">
                <p>You will be redirected to login in 5 seconds.</p>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <button
              type="submit"
              disabled={parentForgotPasswordSuccess}
              className="w-full h-10 inline-flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {parentForgotPasswordLoading ? (
                <div className="flex items-center justify-center w-full">
                  <LoadingSpinner size="sm" className="mr-2" />
                  <span>Loading...</span>
                </div>
              ) : (
                'Reset Password'
              )}
            </button>
          </div>
        </form>
      </Form>
    </AuthLayout>
  );
};

export default ParentDashboardForgotPassword;

// import { MdOutlineMailOutline } from "react-icons/md";
// import React from "react";

// const ParentDashboardForgotPassword = () => {
//   return (
//     <div className="flex min-h-screen">
//       <div className=" lg:block lg:w-1/2 relative">
//         <img
//           src="/admin-bg.png?height=1080&width=1080"
//           alt="Student studying"
//           className="absolute inset-0 h-full w-full object-cover"
//         />
//       </div>
//       <div className="w-full max-w-[50%]  pt-[72px]">
//         <div className="w-full max-w-[576px] mx-auto justify-start">
//           <div className="justify-start ">
//             <img
//               src="/logo.png?height=80&width=80"
//               alt="School logo"
//               className=" h-20 w-20"
//             />
//           </div>

//           <form className="mt-[120px]">
//             <h2 className=" justify-center text-black text-[32px] font-medium font-['Inter'] leading-tight tracking-tight">
//               Forgot Password?
//             </h2>
//             <p className="justify-start mt-6 text-[#bdbdbd] text-2xl font-normal font-['Inter']">
//               No worries, We will send you reset instructions.
//             </p>

//             <div className="flex flex-col gap-3 mt-12">
//               <label htmlFor="email">Email address</label>
//               <div className="border border-[#BFBFBF] flex gap-[28px] items-center px-[9px] py-2 rounded-[5px] w-full max-w-[536px]">
//                 <span>
//                   <MdOutlineMailOutline size={22} />
//                 </span>
//                 <input
//                   type="email"
//                   id="email"
//                   name="email"
//                   placeholder="NarayanMurthy@gmail.com"
//                   className="focus:outline-none w-full"
//                 />
//               </div>
//             </div>

//             <button
//               type="submit"
//               //   disabled={loginLoading}
//               className="w-full mt-[85px] h-10 inline-flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
//             >
//               Reset Password
//               {/* {loginLoading ? (
//             <div className="flex items-center justify-center w-full">
//               <LoadingSpinner size="sm" className="mr-2" />
//               <span>Signing in...</span>
//             </div>
//           ) : (
//             "Sign in"
//           )} */}
//             </button>
//           </form>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ParentDashboardForgotPassword;
